import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderKanban, Music4, Film, Clapperboard, Scissors, Download, Users, LogOut, Bell, AlertTriangle, CreditCard, Coins, Wallet, BookMarked, Sparkles, LayoutDashboard, Tv } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import ProjectsPanel from "@/pages/panels/ProjectsPanel";
import SongPanel from "@/pages/panels/SongPanel";
import VideoPanel from "@/pages/panels/VideoPanel";
import ShortFilmPanel from "@/pages/panels/ShortFilmPanel";
import SeriesPanel from "@/pages/panels/SeriesPanel";
import CastPanel from "@/pages/panels/CastPanel";
import EditorPanel from "@/pages/panels/EditorPanel";
import ExportPanel from "@/pages/panels/ExportPanel";
import CreditsPanel from "@/pages/panels/CreditsPanel";
import ImagePanel from "@/pages/panels/ImagePanel";

const TABS = [
  { id: "proyectos", label: "Proyectos", icon: FolderKanban },
  { id: "imagen", label: "Imagen", icon: Sparkles },
  { id: "cancion", label: "Canción", icon: Music4 },
  { id: "video", label: "Video", icon: Film },
  { id: "corto", label: "Corto", icon: Clapperboard },
  { id: "serie", label: "Serie", icon: Tv },
  { id: "elenco", label: "Biblia", icon: BookMarked },
  { id: "editor", label: "Editor", icon: Scissors },
  { id: "exportar", label: "Exportar", icon: Download },
  { id: "creditos", label: "Créditos", icon: Wallet },
];

export default function Studio() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("proyectos");
  const [projects, setProjects] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [falStatus, setFalStatus] = useState({ enabled: false, out_of_balance: false });
  const [credits, setCredits] = useState(null);
  const [lowCredits, setLowCredits] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const timer = useRef(null);
  const statusRef = useRef({});
  const refundRef = useRef(null);

  // Notify the user when a generation transitions to ready or failed.
  const applyProjects = useCallback((list) => {
    const prev = statusRef.current;
    list.forEach((p) => {
      const before = prev[p.id];
      if (before && before !== "ready" && p.status === "ready") {
        toast.success(`✅ "${p.title}" está listo`);
        setNotifs((n) => [{ id: p.id, title: p.title, type: p.type, at: Date.now() }, ...n].slice(0, 20));
        setUnread((u) => u + 1);
      } else if (before && before !== "failed" && p.status === "failed") {
        toast.error(`"${p.title}" no se pudo generar`);
      }
    });
    statusRef.current = Object.fromEntries(list.map((p) => [p.id, p.status]));
    setProjects(list);
  }, []);

  const reload = useCallback(async () => {
    try {
      const [p, c, f, s] = await Promise.all([
        api.get("/projects"), api.get("/characters"),
        api.get("/fal-status").catch(() => null), api.get("/credits/summary").catch(() => null),
      ]);
      applyProjects(p.data);
      setCharacters(c.data);
      if (f?.data) setFalStatus(f.data);
      if (s?.data) {
        setCredits(s.data.credits);
        setLowCredits(s.data.credits != null && s.data.low_credits != null && s.data.credits <= s.data.low_credits);
        const r = s.data.total_refunded || 0;
        if (refundRef.current != null && r > refundRef.current + 1e-6) {
          toast.success(`💸 Se te reembolsaron ${Math.round(r - refundRef.current)} créditos por un fallo técnico`);
        }
        refundRef.current = r;
      }
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [applyProjects]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => { api.get("/me/admin").then((r) => setIsAdmin(!!r.data.admin)).catch(() => {}); }, []);

  // Poll while any project is processing.
  useEffect(() => {
    const active = projects.some((p) => p.status === "processing" || p.status === "queued" || p.busy);
    if (!active) { if (timer.current) { clearInterval(timer.current); timer.current = null; } return; }
    if (timer.current) return;
    timer.current = setInterval(async () => {
      try { const p = await api.get("/projects"); applyProjects(p.data); } catch {}
    }, 4000);
    return () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  }, [projects, applyProjects]);

  const goTo = (t) => setTab(t);
  const saturated = projects.some((p) => p.status === "processing" && p.retry);
  const openBell = () => { setBellOpen((o) => !o); setUnread(0); };

  const panels = {
    proyectos: <ProjectsPanel projects={projects} characters={characters} reload={reload} goTo={goTo} loading={loading} loadError={loadError} />,
    imagen: <ImagePanel reload={reload} />,
    cancion: <SongPanel projects={projects} reload={reload} />,
    video: <VideoPanel projects={projects} characters={characters} reload={reload} />,
    corto: <ShortFilmPanel projects={projects} characters={characters} reload={reload} />,
    serie: <SeriesPanel projects={projects} characters={characters} reload={reload} />,
    elenco: <CastPanel characters={characters} reload={reload} />,
    editor: <EditorPanel projects={projects} reload={reload} />,
    exportar: <ExportPanel projects={projects} />,
    creditos: <CreditsPanel />,
  };

  return (
    <div className="min-h-screen grain relative pb-24 md:pb-0">
      {/* Header */}
      <header className="glass sticky top-0 z-30 border-b border-white/8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div>
            <Logo size="text-xl" />
            <div className="hidden sm:block text-[0.6rem] font-mono uppercase tracking-widest text-[#64748B] mt-0.5 pl-11">
              Estudio de IA · video y música
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button data-testid="admin-panel-nav" onClick={() => navigate("/panel")}
                title="Panel de control (administrador)"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10 transition-colors">
                <LayoutDashboard className="w-3.5 h-3.5" /> Panel
              </button>
            )}
            <button data-testid="community-nav" onClick={() => navigate("/comunidad")}
              title="Explora la galería de la comunidad"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 text-[#FF9F2E] hover:border-[#FF9F2E] transition-colors">
              <Users className="w-3.5 h-3.5" /> Comunidad
            </button>
            <button data-testid="credits-chip" onClick={() => navigate("/precios")}
              title="Tu saldo de créditos · clic para comprar más"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 text-[#00F0FF] hover:border-[#00F0FF] transition-colors">
              <Coins className="w-3.5 h-3.5" /> {credits != null ? `${credits} cr` : "—"}
            </button>
            {falStatus.enabled && falStatus.balance != null && (
              <a href="https://fal.ai/dashboard/billing" target="_blank" rel="noreferrer" data-testid="fal-balance-chip"
                title="Saldo de tu cuenta de Fal · clic para recargar"
                className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${falStatus.balance < 1 ? "border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10" : "border-white/12 text-[#94A3B8] hover:border-[#FF9F2E] hover:text-[#FF9F2E]"}`}>
                <Coins className="w-3.5 h-3.5" /> Saldo Fal: ${falStatus.balance.toFixed(2)}
              </a>
            )}
            <div className="relative">
              <button data-testid="notif-bell" onClick={openBell}
                className="relative w-9 h-9 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] flex items-center justify-center transition-colors">
                <Bell className="w-4 h-4" />
                {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF334B] text-white text-[0.6rem] font-bold flex items-center justify-center">{unread}</span>}
              </button>
              {bellOpen && (
                <div className="absolute right-0 mt-2 w-72 mfp-card p-3 z-40 glass" data-testid="notif-dropdown">
                  <div className="mono-label mb-2">Listos</div>
                  {notifs.length === 0 ? (
                    <p className="text-sm text-[#64748B] py-3 text-center">Sin avisos todavía.</p>
                  ) : notifs.map((n, i) => (
                    <button key={i} onClick={() => { setTab("proyectos"); setBellOpen(false); }}
                      className="w-full text-left flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-sm truncate">"{n.title}" está listo</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {user?.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-white/15" referrerPolicy="no-referrer" />}
            <span className="hidden sm:block text-sm text-[#94A3B8] max-w-[140px] truncate">{user?.name}</span>
            <button data-testid="logout-button" onClick={logout}
              className="w-9 h-9 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] flex items-center justify-center transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Desktop tabs */}
        <div className="hidden md:block border-t border-white/8">
          <div className="max-w-6xl mx-auto px-6 flex gap-1">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${tab === t.id ? "text-white" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
                {tab === t.id && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#FF334B] rounded-full" />}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {lowCredits && (
          <div data-testid="low-credits-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-4 py-3">
            <Coins className="w-5 h-5 text-[#00F0FF] shrink-0" />
            <p className="text-sm text-[#A5F3FF] flex-1">
              Te quedan <strong>{credits} créditos</strong>. Recarga para seguir creando canciones y videos sin interrupciones — desde $6.99.
            </p>
            <button data-testid="low-credits-topup-btn" onClick={() => navigate("/precios")}
              className="shrink-0 inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#00F0FF] text-black hover:bg-[#5df6ff] transition-colors">
              <Sparkles className="w-4 h-4" /> Recargar créditos
            </button>
          </div>
        )}
        {falStatus.enabled && falStatus.out_of_balance && (
          <div data-testid="balance-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#FF334B]/40 bg-[#FF334B]/10 px-4 py-3">
            <CreditCard className="w-5 h-5 text-[#FF334B] shrink-0" />
            <p className="text-sm text-[#FFB3BC] flex-1">
              Tu cuenta de <strong>Fal</strong> se quedó sin saldo. Recárgala para generar con tu propia cuenta a máxima velocidad. Mientras tanto, la app usa el motor de respaldo automáticamente.
            </p>
            <a data-testid="recharge-fal-btn" href="https://fal.ai/dashboard/billing" target="_blank" rel="noreferrer"
              className="shrink-0 inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF334B] text-white hover:bg-[#e02a40] transition-colors">
              <CreditCard className="w-4 h-4" /> Recargar Fal
            </a>
          </div>
        )}
        {saturated && (
          <div data-testid="service-banner" className="mb-6 flex items-start gap-3 rounded-xl border border-[#FF9F2E]/40 bg-[#FF9F2E]/10 px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-[#FF9F2E] shrink-0 mt-0.5" />
            <p className="text-sm text-[#FF9F2E]">
              El servicio de IA está temporalmente saturado. Tus generaciones se reintentan automáticamente y se completarán en cuanto haya capacidad — es temporal, no pierdes tu lugar.
            </p>
          </div>
        )}
        {panels[tab]}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 glass border-t border-white/8">
        <div className="flex justify-between px-1.5 py-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`bottomnav-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex flex-col items-center gap-1 px-2.5 py-1 min-w-[54px] ${tab === t.id ? "text-[#FF334B]" : "text-[#64748B]"}`}>
              <t.icon className="w-5 h-5" />
              <span className="text-[0.62rem] font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
